import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { useSelector } from "react-redux";
import axios from "axios";
import { useFormik, FormikProvider } from "formik";
import * as Yup from "yup";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  IconButton,
  Box,
  Typography,
  Divider,
  Stack,
  CircularProgress,
  Alert,
  useMediaQuery,
  MenuItem,
  Autocomplete,
  FormControlLabel,
  Switch,
  Tooltip,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import AddIcon from "@mui/icons-material/Add";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import DraggablePaper from "./DraggablePaper";

const validationSchema = Yup.object({
  field_label: Yup.string().required("Field label is required"),
  field_type: Yup.object().required("Field type is required").nullable(),
  location: Yup.string().required("Location is required"),
  parent_field_id: Yup.object()
    .when("field_type", {
      is: (field_type) => field_type?.value === "option",
      then: (schema) => schema.required("Parent field is required"),
      otherwise: (schema) => schema.notRequired(),
    })
    .nullable(),
  multiple: Yup.boolean().nullable(),
  mark_circle: Yup.boolean().nullable(),
  is_default: Yup.boolean().nullable(),
  def_value: Yup.mixed().nullable(),
});

const LOCATION_OPTIONS = [
  { value: "create", label: "Create" },
  { value: "sign", label: "Sign" },
];

export const FIELD_TYPE_OPTIONS = [
  { value: "text", label: "Text" },
  { value: "selectGroup", label: "Select Group" },
  { value: "checkboxGroup", label: "Checkbox Group" },
  { value: "option", label: "Option" },
  { value: "designProAuto", label: "Design Pro Auto" },
  { value: "mobile", label: "Mobile" },
  { value: "email", label: "Email" },
  { value: "dollar", label: "Dollar" },
  { value: "number", label: "Number" },
  { value: "date", label: "Date" },
  { value: "todayDate", label: "Today Date" },
  { value: "addressGroup", label: "Address Group" },
  { value: "mobileGroup", label: "Mobile Group" },
  { value: "multiline", label: "Multiline" },
  { value: "privateProvider", label: "Private Provider" },
  { value: "project_contact_details", label: "Project Contact Details" },
  { value: "designProfessional", label: "Design Professional" },
  { value: "feeSimple", label: "Fee Simple" },
  { value: "bonding", label: "Bonding" },
  { value: "mortgage", label: "Mortgage" },
  { value: "termite", label: "Termite" },
  { value: "full_title", label: "Full Title" },
  { value: "full_subTitle", label: "Full SubTitle" },
  { value: "total", label: "Total" },
  { value: "percentageCheckbox", label: "Percentage Checkbox" },
];

const GROUP_FIELD_TYPES = ["selectGroup", "checkboxGroup"];
const YES_NO_LABELS = ["Yes", "No"];
const NEW_OPTION_PREFIX = "new-";

const isGroupFieldType = (fieldType) => GROUP_FIELD_TYPES.includes(fieldType);

const makeOptionKey = () =>
  `${NEW_OPTION_PREFIX}${Date.now().toString(36)}-${Math.random()
    .toString(36)
    .slice(2, 8)}`;

const createEmptyOption = (label = "") => ({
  key: makeOptionKey(),
  field_id: "",
  field_label: label,
  raw: null,
});

const getInitialDefaultValue = (field) => {
  if (isGroupFieldType(field?.field_type)) {
    if (Array.isArray(field?.def_value)) {
      return field.def_value.filter(Boolean);
    }

    return field?.def_value ? [field.def_value] : [];
  }

  if (Array.isArray(field?.def_value)) {
    return field.def_value[0] ?? "";
  }

  return field?.def_value ?? "";
};

const extractFieldId = (response) =>
  response?.data?.data?.field?.field_id ??
  response?.data?.data?.field_id ??
  response?.data?.field?.field_id ??
  response?.data?.field_id ??
  "";

const NewFieldDialog = ({
  open,
  handleClose,
  getFields,
  selectedField,
  filter,
  searchField,
  setPage,
  getFormFieldsList,
  onFieldCreated,
}) => {
  const mobileSize = useMediaQuery((theme) => theme.breakpoints.down("sm"));
  const currentSession = useSelector((state) => state.sessions.currentSession);

  const [createFieldMessage, setCreateFieldMessage] = useState({
    type: "",
    message: "",
  });
  const [fields, setFields] = useState([]);
  const [fieldsLoaded, setFieldsLoaded] = useState(false);
  const [options, setOptions] = useState([]);
  const [removedOptionIds, setRemovedOptionIds] = useState([]);
  const [optionsError, setOptionsError] = useState("");
  const [progress, setProgress] = useState("");
  const optionsSignatureRef = useRef(null);
  const optionsBoxRef = useRef(null);

  const upsertUrl = `${process.env.REACT_APP_BASE_URL}/nocApi/form-fields/upsert`;
  const deleteUrl = `${process.env.REACT_APP_BASE_URL}/nocApi/form-fields/delete`;

  const getFormFields = useCallback(async () => {
    try {
      const res = await axios.post(
        `${process.env.REACT_APP_BASE_URL}/nocApi/form-fields/list`,
        { currentSession },
        { withCredentials: true },
      );

      if (res.data.success) {
        setFields(res.data.data?.fields ?? []);
      }
    } catch (error) {
      console.error("An error occurred while fetching fields");
    } finally {
      setFieldsLoaded(true);
    }
  }, [currentSession]);

  useEffect(() => {
    getFormFields();
  }, [getFormFields]);

  const existingOptions = useMemo(
    () =>
      fields
        .filter(
          (item) =>
            selectedField?.field_id &&
            item?.parent_field_id === selectedField?.field_id,
        )
        .map((item) => ({
          key: item?.field_id,
          field_id: item?.field_id,
          field_label: item?.field_label ?? "",
          raw: item,
        })),
    [fields, selectedField?.field_id],
  );

  // Load saved options once per open / per selected field, so typing is never wiped.
  useEffect(() => {
    if (!open) {
      optionsSignatureRef.current = null;
      return;
    }

    const signature = `${selectedField?.field_id ?? "new"}:${fieldsLoaded}`;
    if (optionsSignatureRef.current === signature) return;
    optionsSignatureRef.current = signature;

    setOptions(existingOptions.length ? existingOptions : []);
    setRemovedOptionIds([]);
    setOptionsError("");
  }, [open, selectedField?.field_id, fieldsLoaded, existingOptions]);

  const parentFields = useMemo(
    () =>
      fields
        .filter((field) => isGroupFieldType(field?.field_type))
        .map((field) => ({
          label: field?.field_label,
          value: field?.field_id,
        })),
    [fields],
  );

  const formik = useFormik({
    enableReinitialize: true,
    initialValues: {
      field_label: selectedField?.field_label ?? "",
      field_type:
        FIELD_TYPE_OPTIONS?.find(
          (opt) => opt.value === selectedField?.field_type,
        ) ?? null,
      location: selectedField?.location ?? "",
      parent_field_id:
        parentFields.find(
          (field) => field.value === selectedField?.parent_field_id,
        ) ?? null,
      multiple: selectedField?.multiple ?? false,
      mark_circle: selectedField?.mark_circle ?? false,
      is_default: selectedField?.is_default ?? false,
      def_value: getInitialDefaultValue(selectedField),
    },
    validationSchema,
    onSubmit: async (values, { setSubmitting, resetForm }) => {
      setCreateFieldMessage({ type: "", message: "" });
      setOptionsError("");

      const fieldType = values?.field_type?.value || "";
      const isGroup = isGroupFieldType(fieldType);

      const cleanedOptions = options
        .map((option) => ({
          ...option,
          field_label: option.field_label.trim(),
        }))
        .filter((option) => option.field_label);

      if (isGroup) {
        // if (!cleanedOptions.length) {
        //   setOptionsError("Add at least one option to this group.");
        //   setSubmitting(false);
        //   return;
        // }

        const seen = new Set();
        const duplicate = cleanedOptions.find((option) => {
          const key = option.field_label.toLowerCase();
          if (seen.has(key)) return true;
          seen.add(key);
          return false;
        });

        if (duplicate) {
          setOptionsError(`"${duplicate.field_label}" is listed twice.`);
          setSubmitting(false);
          return;
        }
      }

      const selectedDefaultKeys = !values.is_default
        ? []
        : isGroup
          ? [...new Set((values.def_value || []).filter(Boolean))].filter(
              (key) => cleanedOptions.some((option) => option.key === key),
            )
          : [];

      const savedDefaultKeys = selectedDefaultKeys.filter(
        (key) => !String(key).startsWith(NEW_OPTION_PREFIX),
      );
      const hasUnsavedDefault =
        selectedDefaultKeys.length !== savedDefaultKeys.length;

      const defValue = !values.is_default
        ? isGroup
          ? []
          : ""
        : isGroup
          ? savedDefaultKeys
          : String(values.def_value ?? "");

      const payload = {
        ...values,
        field_type: fieldType,
        parent_field_id: values?.parent_field_id?.value || "",
        def_value: defValue,
        ...(selectedField ? { field_id: selectedField?.field_id } : {}),
      };

      if (fieldType !== "option") {
        delete payload.parent_field_id;
      } else {
        delete payload.def_value;
        delete payload.is_default;
      }

      try {
        setProgress(isGroup ? "Saving field…" : "");

        const response = await axios.post(
          upsertUrl,
          { currentSession: currentSession, field: payload },
          { withCredentials: true },
        );

        if (!response?.data?.success) {
          setProgress("");
          setSubmitting(false);
          return;
        }

        let groupId = selectedField?.field_id || extractFieldId(response);

        if (isGroup) {
          // Fallback: some upsert responses don't echo the new id back.
          if (!groupId) {
            const listRes = await axios.post(
              `${process.env.REACT_APP_BASE_URL}/nocApi/form-fields/list`,
              { currentSession },
              { withCredentials: true },
            );
            const allFields = listRes?.data?.data?.fields ?? [];
            groupId =
              [...allFields]
                .reverse()
                .find(
                  (item) =>
                    item?.field_label === values.field_label.trim() &&
                    item?.field_type === fieldType &&
                    item?.location === values.location,
                )?.field_id || "";
          }

          if (!groupId) {
            throw new Error(
              "Field saved, but its options could not be linked. Open the field and add options again.",
            );
          }

          const idMap = {};

          for (let index = 0; index < cleanedOptions.length; index += 1) {
            const option = cleanedOptions[index];
            setProgress(
              `Saving option ${index + 1} of ${cleanedOptions.length}…`,
            );

            const optionPayload = {
              ...(option.raw ?? {}),
              field_label: option.field_label,
              field_type: "option",
              location: values.location,
              parent_field_id: groupId,
              ...(option.field_id ? { field_id: option.field_id } : {}),
            };
            delete optionPayload.def_value;
            delete optionPayload.is_default;

            const optionRes = await axios.post(
              upsertUrl,
              { currentSession, field: optionPayload },
              { withCredentials: true },
            );

            idMap[option.key] =
              option.field_id || extractFieldId(optionRes) || "";
          }

          if (removedOptionIds.length) {
            setProgress("Removing deleted options…");
            for (const removedId of removedOptionIds) {
              // Adjust this endpoint if your delete route differs.
              await axios.post(
                deleteUrl,
                { currentSession, field_id: removedId },
                { withCredentials: true },
              );
            }
          }

          if (values.is_default && hasUnsavedDefault) {
            const resolvedDefaults = selectedDefaultKeys
              .map((key) => idMap[key] || key)
              .filter((id) => id && !String(id).startsWith(NEW_OPTION_PREFIX));

            if (resolvedDefaults.length) {
              setProgress("Saving default value…");
              await axios.post(
                upsertUrl,
                {
                  currentSession,
                  field: {
                    ...payload,
                    field_id: groupId,
                    def_value: values.multiple
                      ? resolvedDefaults
                      : resolvedDefaults.slice(0, 1),
                  },
                },
                { withCredentials: true },
              );
            }
          }
        }

        const searchQuery = selectedField
          ? searchField
          : fieldType === "option"
            ? values.parent_field_id?.label
            : values.field_label.trim();

        setCreateFieldMessage({
          type: "success",
          message:
            response?.data?.message ||
            `Form field ${selectedField ? "updated" : "created"} successfully`,
        });

        resetForm();
        setOptions([]);
        setRemovedOptionIds([]);
        setProgress("");
        getFields(filter, 1, searchQuery);
        if (!selectedField) {
          onFieldCreated?.(searchQuery);
        }
        setPage(1);
        getFormFieldsList();
        handleClose();
      } catch (error) {
        setProgress("");
        setCreateFieldMessage({
          type: "error",
          message:
            error?.response?.data?.message ||
            error?.message ||
            `An error occurred while ${selectedField ? "updating" : "creating"} field`,
        });
      } finally {
        setSubmitting(false);
      }
    },
  });

  const {
    values,
    errors,
    touched,
    handleChange,
    handleBlur,
    handleSubmit,
    isSubmitting,
    resetForm,
    setFieldValue,
  } = formik;

  const currentType = values?.field_type?.value || "";
  const isGroupSelected = isGroupFieldType(currentType);

  const optionChoices = useMemo(
    () =>
      options
        .filter((option) => option.field_label.trim())
        .map((option) => ({
          value: option.key,
          label: option.field_label.trim(),
        })),
    [options],
  );

  const hasYesNo = useMemo(() => {
    const labels = options.map((option) =>
      option.field_label.trim().toLowerCase(),
    );
    return YES_NO_LABELS.every((label) => labels.includes(label.toLowerCase()));
  }, [options]);

  const handleOptionChange = (key, label) => {
    setOptionsError("");
    setOptions((prev) =>
      prev.map((option) =>
        option.key === key ? { ...option, field_label: label } : option,
      ),
    );
  };

  const handleAddOption = () => {
    setOptionsError("");
    setOptions((prev) => [...prev, createEmptyOption()]);

    setTimeout(() => {
      optionsBoxRef.current?.scrollTo({
        top: optionsBoxRef.current.scrollHeight,
        behavior: "smooth",
      });
    }, 0);
  };

  const handleAddYesNo = () => {
    setOptionsError("");
    setOptions((prev) => {
      const existingLabels = new Set(
        prev.map((option) => option.field_label.trim().toLowerCase()),
      );
      const blanks = prev.filter((option) => !option.field_label.trim());
      const filled = prev.filter((option) => option.field_label.trim());
      const additions = YES_NO_LABELS.filter(
        (label) => !existingLabels.has(label.toLowerCase()),
      ).map((label) => createEmptyOption(label));

      // Reuse trailing blank rows instead of stacking empties.
      return [...filled, ...additions, ...blanks.slice(additions.length)];
    });
  };

  const handleRemoveOption = (key) => {
    setOptionsError("");
    setOptions((prev) => {
      const target = prev.find((option) => option.key === key);
      if (target?.field_id) {
        setRemovedOptionIds((ids) =>
          ids.includes(target.field_id) ? ids : [...ids, target.field_id],
        );
      }
      return prev.filter((option) => option.key !== key);
    });

    if (Array.isArray(values.def_value)) {
      setFieldValue(
        "def_value",
        values.def_value.filter((value) => value !== key),
      );
    }
  };

  const handleCloseModal = () => {
    resetForm();
    setOptions([]);
    setRemovedOptionIds([]);
    setOptionsError("");
    setProgress("");
    optionsSignatureRef.current = null;
    setCreateFieldMessage({ type: "", message: "" });
    handleClose();
  };

  return (
    <Dialog
      open={open}
      onClose={handleCloseModal}
      PaperComponent={DraggablePaper}
      aria-labelledby="draggable-dialog-title"
      maxWidth="sm"
      fullWidth
      slotProps={{
        paper: {
          sx: { ...(mobileSize ? { margin: 0, width: "95%" } : {}) },
        },
        backdrop: {
          sx: {
            backgroundColor: "rgba(0, 0, 0, 0.2)",
          },
        },
      }}
    >
      <DialogTitle
        id="draggable-dialog-title"
        style={{ cursor: "move" }}
        sx={{
          px: 3,
          pt: 3,
          pb: 2,
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          gap: 1,
        }}
      >
        <Box>
          <Typography variant="h6" fontWeight={700} lineHeight={1.2}>
            {selectedField ? "Update" : "Create"} Form field
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {isGroupSelected
              ? "Add the group and its options together."
              : "Set the label, type and where the field appears."}
          </Typography>
        </Box>
        <IconButton
          size="small"
          onClick={handleCloseModal}
          disabled={isSubmitting}
          sx={{ mt: -0.5 }}
        >
          <CloseIcon fontSize="small" />
        </IconButton>
      </DialogTitle>
      <Divider />

      <FormikProvider value={formik}>
        <Box component="form" onSubmit={handleSubmit} noValidate>
          <DialogContent sx={{ px: 3, py: 2.5 }}>
            <Stack spacing={2.5}>
              <TextField
                fullWidth
                size="small"
                label="Field Label"
                name="field_label"
                value={values.field_label}
                onChange={handleChange}
                onBlur={handleBlur}
                error={touched.field_label && Boolean(errors.field_label)}
                helperText={touched.field_label && errors.field_label}
              />

              <Stack
                direction={mobileSize ? "column" : "row"}
                spacing={mobileSize ? 2.5 : 2}
              >
                <Autocomplete
                  value={values.field_type}
                  onChange={(event, newValue) => {
                    setFieldValue("field_type", newValue);
                    setFieldValue(
                      "def_value",
                      isGroupFieldType(newValue?.value) ? [] : "",
                    );
                    setFieldValue("is_default", false);
                    setFieldValue("multiple", false);
                    setFieldValue("mark_circle", false);
                    setOptionsError("");

                    if (!isGroupFieldType(newValue?.value)) {
                      setOptions([]);
                    } else if (!options.length) {
                      setOptions(existingOptions.length ? existingOptions : []);
                    }
                  }}
                  onBlur={handleBlur}
                  options={FIELD_TYPE_OPTIONS}
                  fullWidth
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      size="small"
                      label="Select Field Type"
                      name="field_type"
                      error={touched.field_type && Boolean(errors.field_type)}
                      helperText={touched.field_type && errors.field_type}
                    />
                  )}
                />
                <TextField
                  select
                  fullWidth
                  size="small"
                  label="Select Location"
                  name="location"
                  value={values.location}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  error={touched.location && Boolean(errors.location)}
                  helperText={touched.location && errors.location}
                >
                  {LOCATION_OPTIONS.map((opt) => (
                    <MenuItem key={opt.value} value={opt.value}>
                      {opt.label}
                    </MenuItem>
                  ))}
                </TextField>
              </Stack>

              {currentType === "option" && (
                <Autocomplete
                  value={values.parent_field_id}
                  onChange={(event, newValue) => {
                    setFieldValue("parent_field_id", newValue);
                  }}
                  onBlur={handleBlur}
                  options={parentFields}
                  fullWidth
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      size="small"
                      label="Select Parent Field"
                      name="parent_field_id"
                      error={
                        touched.parent_field_id &&
                        Boolean(errors.parent_field_id)
                      }
                      helperText={
                        touched.parent_field_id && errors.parent_field_id
                      }
                    />
                  )}
                />
              )}

              {isGroupSelected && (
                <Box
                  ref={optionsBoxRef}
                  sx={{
                    border: 1,
                    borderColor: optionsError ? "error.main" : "divider",
                    borderRadius: 2,
                    p: 2,

                    maxHeight: 250,
                    overflowY: "auto",
                    overflowX: "hidden",
                  }}
                >
                  <Stack
                    direction="row"
                    alignItems="center"
                    justifyContent="space-between"
                    gap={1}
                    sx={{ mb: 1.5 }}
                  >
                    <Box>
                      <Typography variant="subtitle2" fontWeight={700}>
                        Options
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {options.length
                          ? `${options.length} option${options.length > 1 ? "s" : ""} in this group`
                          : "Saved with the group — no separate field addition is needed."}
                      </Typography>
                    </Box>
                    <Tooltip
                      title={
                        hasYesNo
                          ? "Yes and No are already added"
                          : "Add Yes and No options"
                      }
                    >
                      <span>
                        <Button
                          size="small"
                          variant="outlined"
                          onClick={handleAddYesNo}
                          disabled={isSubmitting || hasYesNo}
                          sx={{
                            textTransform: "none",
                            borderRadius: 2,
                            whiteSpace: "nowrap",
                          }}
                        >
                          Yes / No
                        </Button>
                      </span>
                    </Tooltip>
                  </Stack>

                  <Stack spacing={1.5}>
                    {options.map((option, index) => (
                      <Stack
                        key={option.key}
                        direction="row"
                        spacing={1}
                        alignItems="center"
                      >
                        <TextField
                          fullWidth
                          size="small"
                          label={`Option ${index + 1}`}
                          value={option.field_label}
                          onChange={(e) =>
                            handleOptionChange(option.key, e.target.value)
                          }
                          disabled={isSubmitting}
                          autoFocus={
                            !option.field_id && index === options.length - 1
                          }
                        />
                        <IconButton
                          size="small"
                          onClick={() => handleRemoveOption(option.key)}
                          disabled={isSubmitting}
                          aria-label={`Remove option ${index + 1}`}
                        >
                          <DeleteOutlineIcon fontSize="small" />
                        </IconButton>
                      </Stack>
                    ))}
                  </Stack>

                  <Button
                    size="small"
                    startIcon={<AddIcon fontSize="small" />}
                    onClick={handleAddOption}
                    disabled={isSubmitting}
                    sx={{
                      textTransform: "none",
                      mt: options.length ? 1.5 : 0,
                      alignSelf: "flex-start",
                    }}
                  >
                    Add option
                  </Button>

                  {optionsError && (
                    <Typography
                      variant="caption"
                      color="error"
                      sx={{ display: "block", mt: 1 }}
                    >
                      {optionsError}
                    </Typography>
                  )}
                </Box>
              )}

              {(isGroupSelected || currentType === "option") && (
                <Stack spacing={0.5}>
                  {currentType === "selectGroup" && (
                    <FormControlLabel
                      control={
                        <Switch
                          checked={values.multiple}
                          onChange={(e) => {
                            const allowMultiple = e.target.checked;
                            setFieldValue("multiple", allowMultiple);

                            if (
                              !allowMultiple &&
                              Array.isArray(values.def_value) &&
                              values.def_value.length > 1
                            ) {
                              setFieldValue("def_value", [values.def_value[0]]);
                            }
                          }}
                          disabled={isSubmitting}
                        />
                      }
                      label="Allow Multiple"
                    />
                  )}

                  {isGroupSelected && (
                    <FormControlLabel
                      control={
                        <Switch
                          checked={values.mark_circle}
                          onChange={(e) =>
                            setFieldValue("mark_circle", e.target.checked)
                          }
                          disabled={isSubmitting}
                        />
                      }
                      label="Mark As Circle"
                    />
                  )}
                </Stack>
              )}

              {isGroupSelected &&
                values?.field_type?.value &&
                currentType !== "option" && (
                  <FormControlLabel
                    control={
                      <Switch
                        checked={values.is_default}
                        onChange={(e) => {
                          const isDefault = e.target.checked;
                          setFieldValue("is_default", isDefault);

                          if (!isDefault) {
                            setFieldValue(
                              "def_value",
                              isGroupSelected ? [] : "",
                            );
                          }
                        }}
                        disabled={isSubmitting}
                      />
                    }
                    label="Use a default value"
                  />
                )}

              {values.is_default && isGroupSelected && (
                <Autocomplete
                  multiple={Boolean(values.multiple)}
                  value={
                    values.multiple
                      ? optionChoices.filter((option) =>
                          (values.def_value || []).includes(option.value),
                        )
                      : optionChoices.find(
                          (option) => option.value === values.def_value?.[0],
                        ) || null
                  }
                  onChange={(event, newValue) => {
                    const selectedValues = values.multiple
                      ? newValue.map((option) => option.value)
                      : newValue
                        ? [newValue.value]
                        : [];

                    setFieldValue("def_value", selectedValues);
                  }}
                  onBlur={() => formik.setFieldTouched("def_value", true)}
                  options={optionChoices}
                  isOptionEqualToValue={(option, value) =>
                    option.value === value.value
                  }
                  fullWidth
                  disabled={isSubmitting || optionChoices.length === 0}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      size="small"
                      label="Default Value"
                      name="def_value"
                      helperText={
                        optionChoices.length === 0 &&
                        "Add options above before choosing a default."
                      }
                    />
                  )}
                />
              )}

              {values.is_default &&
                values?.field_type?.value &&
                !isGroupSelected &&
                currentType !== "option" && (
                  <TextField
                    fullWidth
                    size="small"
                    label="Default Value"
                    name="def_value"
                    value={values.def_value}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    disabled={isSubmitting}
                    helperText="Shown until the document has a saved value for this field."
                  />
                )}

              {createFieldMessage?.message && (
                <Alert
                  severity={createFieldMessage?.type || "info"}
                  sx={{ borderRadius: 2 }}
                >
                  {createFieldMessage?.message}
                </Alert>
              )}
            </Stack>
          </DialogContent>

          <Divider />

          <DialogActions
            sx={{
              px: 3,
              py: 2,
              gap: 1,
              justifyContent: progress ? "space-between" : "flex-end",
            }}
          >
            {progress && (
              <Typography variant="caption" color="text.secondary">
                {progress}
              </Typography>
            )}
            <Stack direction="row" gap={1}>
              <Button
                onClick={handleCloseModal}
                variant="text"
                disabled={isSubmitting}
                sx={{ textTransform: "none", color: "text.secondary" }}
              >
                Cancel
              </Button>
              <Button
                type="submit"
                variant="contained"
                disabled={isSubmitting}
                startIcon={
                  isSubmitting ? (
                    <CircularProgress size={14} color="inherit" />
                  ) : null
                }
                sx={{ textTransform: "none", borderRadius: 2, px: 3 }}
              >
                {isSubmitting ? "Saving…" : "Save Field"}
              </Button>
            </Stack>
          </DialogActions>
        </Box>
      </FormikProvider>
    </Dialog>
  );
};

export default NewFieldDialog;
